import { DragDropProvider } from '@dnd-kit/react'
import './loading.css'
import './App.css'
import { Outlet } from '@tanstack/react-router'
import { observer } from 'mobx-react-lite'
import { Suspense, useCallback, useContext, useEffect, useState } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { PuffLoader } from 'react-spinners'
import { Grid } from '~/Components/Grid'
import { useEvictDeadCollapseState } from '~/Helpers/useEvictDeadCollapseState.js'
import { useMountEffect } from '~/Resources/util.js'
import { RootAppStoreContext } from '~/Stores/RootAppStore.js'
import { Button } from './Components/Button.js'
import { Form, InputGroup } from './Components/Form.js'
import { ProgressBar } from './Components/ProgressBar.js'
import { SecretTextInputField } from './Components/SecretTextInputField.js'
import { ContextData } from './ContextData.js'
import { EntityDragLayer } from './Controls/Components/EntityDragLayer.js'
import { TRPCConnectionStatus, useTRPCConnectionStatus } from './Hooks/useTRPCConnectionStatus.js'
import { MyHeader } from './Layout/Header.js'
import { MySidebar, SidebarStateProvider } from './Layout/Sidebar.js'
import { PRIMARY_COLOR } from './Resources/Constants.js'
import { MyErrorBoundary } from './Resources/Error.js'
import { MonacoLoader } from './Resources/MonacoLoader.js'
import { SortableHysteresis } from './Resources/SortableHysteresis.js'
import { shouldAutoOpenWizard } from './Wizard/Constants.js'
import { WizardModal } from './Wizard/index.js'

export default function App(): React.JSX.Element {
	const trpcStatus = useTRPCConnectionStatus()

	const connected = trpcStatus.status === TRPCConnectionStatus.Connected
	const wasConnected = trpcStatus.wasConnected
	const shouldReload = connected && wasConnected

	useEffect(() => {
		if (shouldReload) {
			console.log('Reloading page after TRPC reconnect')
			// Reload the page to ensure that the UI is up-to-date and we don't have any stale data
			window.location.reload()
		}
	}, [shouldReload])

	return (
		<ContextData>
			{(loadingProgress, loadingComplete) => (
				<>
					<div id="error-container" className={wasConnected ? 'show-error' : ''}>
						<Grid.Row>
							<Grid.Col md={{ span: 6, offset: 3 }}>
								<div className="clearfix">
									<h4 className="pt-4">Houston, we have a problem!</h4>
									<p className="text-muted">It seems that we have lost connection to the companion app.</p>
									<ul className="text-muted">
										<li>Check that the application is still running</li>
										<li>If you're using the Admin GUI over a network - check your connection</li>
									</ul>
								</div>
							</Grid.Col>
						</Grid.Row>
					</div>
					<ImportTaskOverlay wasConnected={wasConnected} />
					<Suspense
						fallback={
							<Grid.Row className={'loading'}>
								<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
									<PuffLoader loading={true} size={80} color={PRIMARY_COLOR} />
								</div>
							</Grid.Row>
						}
					>
						<MonacoLoader />
						{/*
						 * Single global dnd-kit provider for all drag and drop. Each feature subscribes to its
						 * own drags via useDragDropMonitor() and filters by drag `type`, so handlers stay scoped
						 * while dragging between different parts of the UI remains possible (one shared manager).
						 * Feedback mode is configured per-draggable where needed (e.g. presets drag a clone with
						 * no drop animation - see PresetIconPreview); everything else uses the defaults.
						 */}
						<DragDropProvider>
							<SortableHysteresis />
							<EntityDragLayer />
							<AppMain
								connected={connected && !shouldReload}
								loadingComplete={loadingComplete}
								loadingProgress={loadingProgress}
							/>
						</DragDropProvider>
					</Suspense>
				</>
			)}
		</ContextData>
	)
}

// Reads the shared import/reset task status (driven by useImportTaskStatusSubscription) and shows the
// blocking overlay on still-connected clients while a task runs. A dropped client sees the
// disconnect screen instead, and reloads on reconnect.
const ImportTaskOverlay = observer(function ImportTaskOverlay({ wasConnected }: { wasConnected: boolean }) {
	const { importTaskStatus } = useContext(RootAppStoreContext)
	const taskRunning = importTaskStatus.get()?.status === 'running'

	return (
		<div id="current-import-container" className={!wasConnected && taskRunning ? 'show-error' : ''}>
			<Grid.Row>
				<Grid.Col md={{ span: 6, offset: 3 }}>
					<div className="clearfix">
						<h4 className="pt-4">Stand by, the config is being updated!</h4>
					</div>
				</Grid.Col>
			</Grid.Row>
		</div>
	)
})

interface AppMainProps {
	connected: boolean
	loadingComplete: boolean
	loadingProgress: number
}

const AppMain = observer(function AppMain({ connected, loadingComplete, loadingProgress }: AppMainProps) {
	const { userConfig, wizardOpen } = useContext(RootAppStoreContext)

	// Once everything has loaded, prune collapse-state keys for controls/connections that no longer exist
	useEvictDeadCollapseState(loadingComplete)

	const [unlocked, setUnlocked] = useState(false)

	const canLock = !!userConfig.properties?.admin_lockout
	const setLocked = useCallback(() => {
		if (canLock) {
			setUnlocked(false)
		}
	}, [canLock])

	// const wizardModal = useRef<WizardModalRef>(null)
	// const showWizard = useCallback(() => {
	// 	if (unlocked) {

	// 		wizardModal.current?.show()
	// 	}
	// }, [unlocked])

	const setup_wizard = userConfig.properties?.setup_wizard
	const setUnlockedInner = useCallback(() => {
		setUnlocked(true)
		if (shouldAutoOpenWizard(setup_wizard)) {
			wizardOpen.set(true)
		}
	}, [setup_wizard, wizardOpen])

	// If lockout is disabled, then we are logged in
	const admin_lockout = userConfig.properties && !userConfig.properties?.admin_lockout
	useEffect(() => {
		if (admin_lockout) {
			setUnlocked(true)
			if (shouldAutoOpenWizard(setup_wizard)) {
				wizardOpen.set(true)
			}
		}
	}, [admin_lockout, setup_wizard, wizardOpen])

	return (
		<div className="c-app">
			<SidebarStateProvider>
				{canLock && unlocked && (userConfig.properties?.admin_timeout ?? 0) > 0 ? (
					<IdleTimerWrapper setLocked={setLocked} timeoutMinutes={userConfig.properties?.admin_timeout} />
				) : (
					''
				)}
				<MySidebar />
				<div className="wrapper flex flex-col min-h-screen bg-app-frame-bg">
					<MyHeader setLocked={setLocked} canLock={canLock && unlocked} />
					<div className="body grow">
						{connected && loadingComplete ? (
							!canLock || unlocked ? (
								<AppContent />
							) : (
								<AppAuthWrapper setUnlocked={setUnlockedInner} />
							)
						) : (
							<AppLoading progress={loadingProgress} connected={connected} />
						)}
					</div>
				</div>
			</SidebarStateProvider>
		</div>
	)
})

interface IdleTimerWrapperProps {
	setLocked: () => void
	timeoutMinutes: number
}

/** Wrap the idle timer in its own component, as it invalidates every second */
function IdleTimerWrapper({ setLocked, timeoutMinutes }: IdleTimerWrapperProps) {
	const { notifier } = useContext(RootAppStoreContext)

	const [, setIdleTimeout] = useState<NodeJS.Timeout | null>(null)

	const TOAST_ID = 'SESSION_TIMEOUT_TOAST'
	const TOAST_DURATION = 45 * 1000

	const handleOnActive = () => {
		// user is now active, abort the lock
		setIdleTimeout((v) => {
			if (v) {
				clearTimeout(v)
			}

			// close toast
			notifier.close(TOAST_ID)

			return null
		})
	}
	const handleAction = () => {
		// setShouldShowIdleWarning(false)
	}

	const handleIdle = () => {
		notifier.show(
			'Session timeout',
			'Your session is about to timeout, and Companion will be locked',
			undefined,
			TOAST_ID
		)

		setIdleTimeout((v) => {
			if (!v) {
				return setTimeout(() => {
					// close toast
					notifier.close(TOAST_ID)

					setLocked()
				}, TOAST_DURATION)
			}

			return v
		})
	}

	const cappedTimeout = Math.min(timeoutMinutes, 24 * 60) // cap to 24 hours

	useIdleTimer({
		timeout: cappedTimeout * 60 * 1000 - TOAST_DURATION,
		onIdle: handleIdle,
		onActive: handleOnActive,
		onAction: handleAction,
		debounce: 500,
	})

	useMountEffect(() => {
		return () => {
			setIdleTimeout((v) => {
				if (v) {
					clearTimeout(v)
				}
				return null
			})

			// close toast
			notifier.close(TOAST_ID)
		}
	})

	return null
}

interface AppLoadingProps {
	progress: number
	connected: boolean
}

function AppLoading({ progress, connected }: AppLoadingProps) {
	const message = connected ? 'Syncing' : 'Connecting'
	return (
		<Grid.Container className="fadeIn loading">
			<Grid.Row>
				<Grid.Col xxl={4} md={3} sm={2} xs={1}></Grid.Col>
				<Grid.Col xxl={4} md={6} sm={8} xs={10}>
					<h3>{message}</h3>
					{connected ? (
						<ProgressBar className="mt-6" value={progress} />
					) : (
						<div className="flex items-center justify-center mt-6">
							<PuffLoader loading={true} size={80} color={PRIMARY_COLOR} />
						</div>
					)}
				</Grid.Col>
			</Grid.Row>
		</Grid.Container>
	)
}

interface AppAuthWrapperProps {
	setUnlocked: () => void
}

const AppAuthWrapper = observer(function AppAuthWrapper({ setUnlocked }: AppAuthWrapperProps) {
	const { userConfig } = useContext(RootAppStoreContext)

	const [password, setPassword] = useState('')
	const [showError, setShowError] = useState(false)

	const passwordChanged = useCallback((newValue: string) => {
		setPassword(newValue)
		setShowError(false)
	}, [])

	const tryLogin = useCallback(
		(e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault()

			setPassword((currentPassword) => {
				if (currentPassword === userConfig.properties?.admin_password) {
					setShowError(false)
					setUnlocked()
					return ''
				} else {
					setShowError(true)
					// preserve current entered value
					return currentPassword
				}
			})

			return false
		},
		[userConfig, setUnlocked]
	)

	return (
		<Grid.Container className="fadeIn loading">
			<Grid.Row>
				<Grid.Col xxl={4} md={3} sm={2} xs={1}></Grid.Col>
				<Grid.Col xxl={4} md={6} sm={8} xs={10}>
					<h3>Companion is locked</h3>
					<Form onSubmit={tryLogin}>
						<InputGroup>
							<SecretTextInputField
								id={undefined}
								value={password}
								setValue={passwordChanged}
								checkValid={showError ? false : undefined}
								immediateValue
							/>
							<Button type="submit" color="primary">
								Unlock
							</Button>
						</InputGroup>
					</Form>
				</Grid.Col>
			</Grid.Row>
		</Grid.Container>
	)
})

const AppContent = observer(function AppContent() {
	const { userConfig } = useContext(RootAppStoreContext)

	useEffect(() => {
		document.title =
			userConfig.properties?.installName && userConfig.properties?.installName.length > 0
				? `${userConfig.properties?.installName} - Admin (Bitfocus Companion)`
				: 'Bitfocus Companion - Admin'
	}, [userConfig.properties?.installName])

	return (
		<Grid.Container className="fadeIn">
			<WizardModal />

			<MyErrorBoundary>
				<Outlet />
			</MyErrorBoundary>
		</Grid.Container>
	)
})
